import { useEffect, useState } from 'react';
import { useMenuNames } from '../lib/store';

interface MenuActionsSheetProps {
  open: boolean;
  onClose: () => void;
  menuId: string;
  // Current display name (custom rename if set, otherwise the detected name).
  // Drives share title and prefills the rename form.
  restaurantName: string | null;
  // The Gemini-detected name, shown as a hint under the rename input so the
  // user knows what "clearing the input" falls back to.
  detectedName: string | null;
  hasOriginalPhoto: boolean;
  onViewOriginal: () => void;
}

type Mode = 'actions' | 'renaming';

// Secondary actions for the menu view (rename, share, view original photo).
// Keeps the header clean — browsing controls stay inline, these are one tap
// away. The rename form replaces the action list in-place rather than
// pushing a new dialog: fewer focus/back-button seams on mobile.
export function MenuActionsSheet({
  open,
  onClose,
  menuId,
  restaurantName,
  detectedName,
  hasOriginalPhoto,
  onViewOriginal,
}: MenuActionsSheetProps) {
  const [copied, setCopied] = useState(false);
  const [mode, setMode] = useState<Mode>('actions');
  const [draft, setDraft] = useState(restaurantName ?? '');
  const setMenuName = useMenuNames((s) => s.setName);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  // Reset to action list + re-seed draft whenever the sheet re-opens or the
  // source name changes underneath us.
  useEffect(() => {
    if (open) {
      setMode('actions');
      setDraft(restaurantName ?? '');
    }
  }, [open, restaurantName]);

  // Body scroll lock while the sheet is up — stops the menu behind from
  // scrolling on drag when the user pulls outside the sheet panel.
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, [open]);

  if (!open) return null;

  const share = async () => {
    const url = `${window.location.origin}/menu/${menuId}`;
    const title = restaurantName ? `Menu: ${restaurantName}` : 'Menu to Menu';
    const nav = navigator as Navigator & { share?: (data: { title: string; url: string }) => Promise<void> };
    if (nav.share) {
      try {
        await nav.share({ title, url });
        onClose();
        return;
      } catch {
        // user dismissed share — fall through to clipboard
      }
    }
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => { setCopied(false); onClose(); }, 1200);
    } catch {
      window.prompt('Copy this link', url);
      onClose();
    }
  };

  const saveRename = () => {
    // Empty/whitespace clears the override — the menu falls back to the
    // detected name in the display layer.
    setMenuName(menuId, draft);
    onClose();
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      style={{ position: 'fixed', inset: 0, zIndex: 60, display: 'flex', flexDirection: 'column', justifyContent: 'flex-end' }}
    >
      <div
        onClick={onClose}
        style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.4)', backdropFilter: 'blur(2px)' }}
      />
      <div
        style={{
          position: 'relative',
          background: 'var(--color-bg)',
          borderTopLeftRadius: 20,
          borderTopRightRadius: 20,
          padding: '8px 12px max(16px, env(safe-area-inset-bottom))',
          boxShadow: 'var(--shadow-sheet)',
        }}
      >
        <div
          aria-hidden
          style={{ width: 40, height: 4, borderRadius: 2, background: 'var(--color-divider)', margin: '8px auto 12px' }}
        />
        {mode === 'actions' && (
          <>
            <button
              onClick={() => setMode('renaming')}
              style={sheetItem()}
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                <path d="M12 20h9" />
                <path d="M16.5 3.5a2.121 2.121 0 1 1 3 3L7 19l-4 1 1-4Z" />
              </svg>
              <span>Rename menu</span>
            </button>
            <button
              onClick={share}
              style={sheetItem()}
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="18" cy="5" r="3" />
                <circle cx="6" cy="12" r="3" />
                <circle cx="18" cy="19" r="3" />
                <line x1="8.59" y1="13.51" x2="15.42" y2="17.49" />
                <line x1="15.41" y1="6.51" x2="8.59" y2="10.49" />
              </svg>
              <span>{copied ? 'Link copied' : 'Share menu'}</span>
            </button>
            {hasOriginalPhoto && (
              <button
                onClick={() => { onViewOriginal(); onClose(); }}
                style={sheetItem()}
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="3" y="3" width="18" height="18" rx="2" />
                  <circle cx="9" cy="9" r="2" />
                  <path d="m21 15-3.086-3.086a2 2 0 0 0-2.828 0L6 21" />
                </svg>
                <span>View original menu photo</span>
              </button>
            )}
          </>
        )}

        {mode === 'renaming' && (
          <form
            onSubmit={(e) => { e.preventDefault(); saveRename(); }}
            style={{ padding: '8px 4px 4px', display: 'flex', flexDirection: 'column', gap: 12 }}
          >
            <label
              htmlFor="menu-rename-input"
              style={{ fontSize: 13, fontWeight: 500, color: 'var(--color-text-secondary)' }}
            >
              Name this menu
            </label>
            <input
              id="menu-rename-input"
              type="text"
              autoFocus
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              placeholder={detectedName ?? 'Menu'}
              style={{
                width: '100%',
                padding: '12px 14px',
                borderRadius: 12,
                border: '1px solid var(--color-divider)',
                background: 'var(--color-surface)',
                color: 'var(--color-primary)',
                fontSize: 16,
                fontFamily: 'inherit',
              }}
            />
            {detectedName && (
              <p style={{ margin: 0, fontSize: 12, color: 'var(--color-text-muted)', lineHeight: 1.4 }}>
                Detected: {detectedName}. Leave empty to use the detected name.
              </p>
            )}
            <div style={{ display: 'flex', gap: 8, marginTop: 4 }}>
              <button
                type="button"
                onClick={() => setMode('actions')}
                style={secondaryBtn}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="btn-primary"
                style={{ flex: 1 }}
              >
                Save
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}

function sheetItem(): React.CSSProperties {
  return {
    display: 'flex',
    alignItems: 'center',
    gap: 14,
    width: '100%',
    padding: '14px 16px',
    background: 'transparent',
    border: 'none',
    color: 'var(--color-primary)',
    fontSize: 15,
    fontWeight: 500,
    textAlign: 'left',
    borderRadius: 10,
    minHeight: 48,
  };
}

const secondaryBtn: React.CSSProperties = {
  flex: 1,
  padding: '14px 20px',
  borderRadius: 12,
  border: '1px solid var(--color-divider)',
  background: 'transparent',
  color: 'var(--color-primary)',
  fontSize: 16,
  fontWeight: 500,
  cursor: 'pointer',
  minHeight: 56,
};
